import { InfoIcon } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import API from '@/api/api'
import { useConnectRemoteLockUser } from '@/api/keyless/remote-lock/connect-remote-lock-user'
import { useCreateRemoteLockUser } from '@/api/keyless/remote-lock/create-remote-lock-user'
import { useGetRemoteLockDevices } from '@/api/keyless/remote-lock/get-remote-lock-devices'
import { useGetRemoteLockDisconnectedUsers } from '@/api/keyless/remote-lock/get-remote-lock-disconnected-users'
import { useGetRemoteLockUserDevices } from '@/api/keyless/remote-lock/get-remote-lock-user-devices'
import { useReassignRemoteLockDoors } from '@/api/keyless/remote-lock/reassign-remote-lock-doors'
import { useIsRemoteLockBuildingConnected } from '@/api/property-keyless/remote-lock/use-is-remote-lock-building-connected'
import RemoteLockIssueFobModal from '@/components/Clients/ClientProfileView/IntegrationsSection/RemoteLockIssueFobModal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChipSelect } from '@/components/ui/chip-select'
import { Collapse } from '@/components/ui/collapse'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Flex } from '@/components/ui/flex'
import { Select } from '@/components/ui/select'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory, toastFactory } from '@/factories'
import { formatDateOnly } from '@/utils/date'
import { getApiErrorMessage } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { computeKeylessArrayDiff } from '../keyless-access.utils'
import { KeylessBuildingNotConnected } from '../keyless-access-building-not-connected'
import { KeylessNotConnected } from '../keyless-access-not-connected'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'
import { RemoteLockTabProps } from './remote-lock-tab.types'

const FOB_TOOLTIP_TEXT =
  'The physical Fob/Card could be issued only if the resident already has assigned credentials within the Remote Lock system. If still not, they can be specified in a block above where you create a new Remote Lock user, connect to an existing Remote Lock user, or doors, zones, access levels and expiration date could be assigned.'

export const RemoteLockTab = ({
  clientId,
  clientUserId,
  buildingId,
  remoteLockFobAvailable,
  isEditing,
  onEditingChange,
  onRegisterSave,
  onRegisterGetChanges,
  onLoadingChange,
  onConnectionChange,
}: RemoteLockTabProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const [selectedDoorIds, setSelectedDoorIds] = useState<string[] | null>(null)
  const [showDisconnectedList, setShowDisconnectedList] = useState(false)
  const [selectedConnectUserId, setSelectedConnectUserId] = useState<string | null>(null)
  const [isFobModalOpen, setIsFobModalOpen] = useState(false)
  const [fobIssuedDate, setFobIssuedDate] = useState<string | null>(null)

  const {
    data: userDevices,
    isLoading: isLoadingUser,
    isFetching: isFetchingUser,
    isSuccess: isUserDevicesSuccess,
    refetch: refetchUser,
  } = useGetRemoteLockUserDevices({ clientId })
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsRemoteLockBuildingConnected(buildingId)
  const { data: allDevices = [], isLoading: isLoadingDevices } = useGetRemoteLockDevices(
    { buildingId },
    Boolean(userDevices),
  )
  const {
    data: disconnectedUsers = [],
    isFetching: isFetchingDisconnected,
    refetch: refetchDisconnected,
  } = useGetRemoteLockDisconnectedUsers({ buildingId }, showDisconnectedList)

  const { mutateAsync: createUser, isPending: isCreating } = useCreateRemoteLockUser()
  const { mutateAsync: connectUser } = useConnectRemoteLockUser()
  const { mutateAsync: reassignDoors } = useReassignRemoteLockDoors()

  // Note: gated on `isSuccess`, not just the raw response - React Query keeps the last
  // successful value in `data` even after a later background refetch fails, which would
  // otherwise leave this resident looking connected once a stale value was ever fetched.
  const isConnected = isUserDevicesSuccess && Boolean(userDevices)
  const isTabLoading = isLoadingUser || isFetchingUser || isLoadingBuildingConnection

  useEffect(() => {
    onLoadingChange?.(isTabLoading)
    return () => onLoadingChange?.(false)
  }, [isTabLoading, onLoadingChange])

  useEffect(() => {
    if (isLoadingUser || isLoadingBuildingConnection) return
    onConnectionChange?.(isBuildingConnected && isConnected)
  }, [
    isConnected,
    isBuildingConnected,
    isLoadingUser,
    isLoadingBuildingConnection,
    onConnectionChange,
  ])

  useEffect(() => {
    if (userDevices?.fobIssuedAt) {
      setFobIssuedDate(formatDateOnly(new Date(userDevices.fobIssuedAt)))
    }
  }, [userDevices?.fobIssuedAt])

  const handleReassignDoors = async () => {
    try {
      await reassignDoors({
        clientId,
        lockIds: selectedDoorIds ?? (userDevices?.accesses ?? []).map((a) => a.id),
      })
      toastFactory.success(t('clients:keylessAccess.remoteLock.toasts.reassignSuccess'))
      void refetchUser()
      onEditingChange(false)
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleOpenReassignConfirm = () => {
    if (selectedDoorIds === null) {
      onEditingChange(false)
      return
    }
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.remoteLock.reassignDoors.confirmBody'),
      confirmText: t('clients:keylessAccess.remoteLock.reassignDoors.confirmText'),
      onConfirm: handleReassignDoors,
      title: t('clients:keylessAccess.remoteLock.reassignDoors.confirmTitle'),
      variant: 'warning',
    })
  }

  useEffect(() => {
    if (!isEditing) {
      setSelectedDoorIds(null)
      return
    }
    onRegisterSave?.(handleOpenReassignConfirm)
    return () => onRegisterSave?.(null)
  }, [isEditing, onRegisterSave, selectedDoorIds, onEditingChange])

  const currentValuesRef = useRef({ allDevices, selectedDoorIds, userDevices })
  currentValuesRef.current = { allDevices, selectedDoorIds, userDevices }
  const initialDoorIdsRef = useRef<string[]>([])

  useEffect(() => {
    if (!isEditing) {
      onRegisterGetChanges?.(null)
      return
    }
    initialDoorIdsRef.current = (currentValuesRef.current.userDevices?.accesses ?? []).map(
      (a) => a.id,
    )
    onRegisterGetChanges?.(() => {
      const {
        allDevices: devices,
        selectedDoorIds: sel,
        userDevices: ud,
      } = currentValuesRef.current
      const initial = initialDoorIdsRef.current
      const current = sel ?? (ud?.accesses ?? []).map((a) => a.id)
      const diff = computeKeylessArrayDiff(
        initial,
        current,
        'Door',
        (id) => devices.find((d) => d.id === id)?.name ?? id,
      )
      return diff ? [diff] : []
    })
    return () => onRegisterGetChanges?.(null)
  }, [isEditing, onRegisterGetChanges])

  const doorOptions = allDevices.map((d) => ({ label: d.name, value: d.id }))

  const handleCreateNewUser = async () => {
    try {
      await createUser({ clientId })
      toastFactory.success(t('clients:keylessAccess.remoteLock.toasts.createSuccess'))
      void refetchUser()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleFetchConnectList = async () => {
    const result = await refetchDisconnected()
    const users = result.data ?? []
    if (users.length === 0) {
      toastFactory.error(t('clients:keylessAccess.remoteLock.toasts.noAccountsToConnect'))
    } else {
      setShowDisconnectedList(true)
    }
  }

  const handleConnectConfirmed = async () => {
    if (!selectedConnectUserId) return
    try {
      await connectUser({ clientId, remoteLockId: selectedConnectUserId })
      toastFactory.success(t('clients:keylessAccess.remoteLock.toasts.connectSuccess'))
      setShowDisconnectedList(false)
      void refetchUser()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleOpenCreateConfirm = () => {
    setShowDisconnectedList(false)
    setSelectedConnectUserId(null)
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.remoteLock.createUser.confirmBody'),
      confirmText: t('clients:keylessAccess.remoteLock.createUser.confirmText'),
      onConfirm: handleCreateNewUser,
      title: t('clients:keylessAccess.remoteLock.createUser.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleOpenConnectConfirm = () => {
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.remoteLock.connectUser.confirmBody'),
      confirmText: t('clients:keylessAccess.remoteLock.connectUser.confirmText'),
      onConfirm: handleConnectConfirmed,
      title: t('clients:keylessAccess.remoteLock.connectUser.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleFobSuccess = () => {
    setFobIssuedDate(formatDateOnly(new Date()))
  }

  const handleRevokeFob = async () => {
    try {
      await API.revokeRemoteLockFob(buildingId, Number(clientUserId))
      setFobIssuedDate(null)
      toastFactory.success('Fob/Card has been successfully revoked.')
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleOpenRevokeConfirm = () => {
    popupFactory.open(ConfirmationPopup, {
      children: 'Are you sure you want to revoke the Fob/Card?',
      confirmText: 'Yes, revoke',
      onConfirm: handleRevokeFob,
      title: 'Caution',
      variant: 'warning',
    })
  }

  if (isTabLoading) return <KeylessTabSkeleton rowCount={1} />

  if (!isBuildingConnected) {
    return (
      <KeylessBuildingNotConnected
        title={t('clients:keylessAccess.remoteLock.buildingNotConnected.title')}
        message={t('clients:keylessAccess.remoteLock.buildingNotConnected.message')}
      />
    )
  }

  const fobBlock = remoteLockFobAvailable ? (
    <S.FobSection>
      <S.FobSectionTitle>
        <Typography size="text-medium" weight="medium" color="black800" as="span">
          Issue a Remote Lock physical Fob/Card
        </Typography>
        <Tooltip triggerIcon={InfoIcon}>{FOB_TOOLTIP_TEXT}</Tooltip>
      </S.FobSectionTitle>
      <S.TabContent>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              Fob/Card
            </Typography>
          </Flex>
          <Flex gap={12} justify="flex-end" align="center">
            {fobIssuedDate ? (
              <>
                <Typography size="text-medium" color="black800" as="span" whiteSpace="nowrap">
                  Fob/Card issued on {fobIssuedDate}
                </Typography>
                <Button size="small" use="danger" onClick={handleOpenRevokeConfirm}>
                  Revoke Fob/Card
                </Button>
              </>
            ) : (
              <Button size="small" disabled={!isConnected} onClick={() => setIsFobModalOpen(true)}>
                Issue Fob/Card
              </Button>
            )}
          </Flex>
        </S.FieldRow>
      </S.TabContent>
      <RemoteLockIssueFobModal
        isOpen={isFobModalOpen}
        buildingId={buildingId}
        userId={Number(clientUserId)}
        onClose={() => setIsFobModalOpen(false)}
        onSuccess={handleFobSuccess}
      />
    </S.FobSection>
  ) : null

  if (!isConnected) {
    const connectOptions = disconnectedUsers.map((u) => ({ label: u.name, value: u.id }))
    return (
      <>
        <S.TabContent>
          <KeylessNotConnected
            label={t('clients:keylessAccess.remoteLock.label')}
            isCreating={isCreating}
            isConnecting={isFetchingDisconnected}
            isConnectDisabled={showDisconnectedList}
            onCreateNew={handleOpenCreateConfirm}
            onConnectExisting={() => void handleFetchConnectList()}
          />
          <Collapse in={showDisconnectedList} animation="grow">
            <S.FieldRow>
              <Flex gap={6} align="center">
                <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                  {t('clients:keylessAccess.remoteLock.selectUser.label')}
                </Typography>
              </Flex>
              <Flex gap={12} justify="flex-end" align="center">
                <Select
                  size="small"
                  options={connectOptions}
                  value={selectedConnectUserId}
                  onChange={(value: string) => setSelectedConnectUserId(value)}
                  onReset={() => setSelectedConnectUserId(null)}
                />
                <Button
                  size="small"
                  disabled={!selectedConnectUserId}
                  onClick={handleOpenConnectConfirm}
                >
                  {t('common:actions.confirm')}
                </Button>
              </Flex>
            </S.FieldRow>
          </Collapse>
        </S.TabContent>
        {fobBlock}
      </>
    )
  }

  const activeDoorIds = selectedDoorIds ?? (userDevices?.accesses ?? []).map((a) => a.id)
  const selectedDoors = allDevices.filter((d) => activeDoorIds.includes(d.id))

  if (!isEditing) {
    return (
      <>
        <S.TabContent>
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.remoteLock.door.label')}
              </Typography>
            </Flex>
            <S.FieldValue>
              {selectedDoors.length > 0 ? (
                <Flex gap={6} wrap="wrap">
                  {selectedDoors.map((d) => (
                    <Badge key={d.id} color="blue" size="small" variant="light">
                      {d.name}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                EMPTY_VALUE
              )}
            </S.FieldValue>
          </S.FieldRow>
        </S.TabContent>
        {fobBlock}
      </>
    )
  }

  return (
    <>
      <S.TabContent>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.remoteLock.door.label')}
            </Typography>
          </Flex>
          <ChipSelect
            size="small"
            options={doorOptions}
            value={activeDoorIds}
            placeholder={t('clients:keylessAccess.remoteLock.door.placeholder')}
            loading={isLoadingDevices}
            onChange={(ids) => setSelectedDoorIds(ids)}
          />
        </S.FieldRow>
      </S.TabContent>
      {fobBlock}
    </>
  )
}
